import React, { createContext, useContext, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { SparePart, MoldSparePart, TransactionType } from '@/types/spareParts';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchSpareParts,
  fetchAllMoldSpareParts,
  addTransactionInDB,
  linkPartToMoldInDB,
} from '@/lib/api/spareParts';

// ─── Context types ────────────────────────────────────────────────────────────

interface SparePartsContextType {
  parts: SparePart[];
  moldParts: (MoldSparePart & { part: SparePart })[];
  loading: boolean;
  getPart: (id: string) => SparePart | undefined;
  getPartsByMold: (moldId: string) => (MoldSparePart & { part: SparePart })[];
  getMoldsByPart: (partId: string) => MoldSparePart[];
  addTransaction: (
    partId: string,
    type: TransactionType,
    qty: number,
    opts?: { woId?: string; moldId?: string; notes?: string },
  ) => Promise<void>;
  linkPartToMold: (moldId: string, partId: string, notes?: string) => Promise<void>;
}

const SparePartsContext = createContext<SparePartsContextType | null>(null);

const PARTS_KEY = ['spare_parts'] as const;
const MOLD_PARTS_KEY = ['mold_spare_parts'] as const;

// ─── Provider ─────────────────────────────────────────────────────────────────

export function SparePartsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.organization_id ?? null;

  const { data: parts = [], isLoading: partsLoading } = useQuery({
    queryKey: [...PARTS_KEY, orgId],
    queryFn: () => fetchSpareParts(orgId),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const { data: moldParts = [], isLoading: moldPartsLoading } = useQuery({
    queryKey: [...MOLD_PARTS_KEY, orgId],
    queryFn: () => fetchAllMoldSpareParts(orgId),
    enabled: !!user,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  const loading = partsLoading || moldPartsLoading;

  // ── Reads ──────────────────────────────────────────────────────────────────

  const getPart = useCallback(
    (id: string) => parts.find(p => p.id === id),
    [parts],
  );

  const getPartsByMold = useCallback(
    (moldId: string) => moldParts.filter(mp => mp.mold_id === moldId),
    [moldParts],
  );

  const getMoldsByPart = useCallback(
    (partId: string) => moldParts.filter(mp => mp.part_id === partId),
    [moldParts],
  );

  // ── Mutations ──────────────────────────────────────────────────────────────

  const txnMutation = useMutation({
    mutationFn: ({ partId, type, qty, opts }: {
      partId: string; type: TransactionType; qty: number;
      opts?: { woId?: string; moldId?: string; notes?: string };
    }) => addTransactionInDB(partId, { type, qty, userId: user!.id, ...opts }, orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PARTS_KEY });
    },
    onError: (err: Error) => toast.error(`Transaction failed: ${err.message}`),
  });

  const addTransaction = useCallback(
    (partId: string, type: TransactionType, qty: number, opts?: { woId?: string; moldId?: string; notes?: string }) =>
      txnMutation.mutateAsync({ partId, type, qty, opts }),
    [txnMutation],
  );

  const linkMutation = useMutation({
    mutationFn: ({ moldId, partId, notes }: { moldId: string; partId: string; notes?: string }) =>
      linkPartToMoldInDB(moldId, partId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MOLD_PARTS_KEY });
    },
    onError: (err: Error) => toast.error(`Failed to link part: ${err.message}`),
  });

  const linkPartToMold = useCallback(
    (moldId: string, partId: string, notes?: string) =>
      linkMutation.mutateAsync({ moldId, partId, notes }),
    [linkMutation],
  );

  return (
    <SparePartsContext.Provider value={{
      parts, moldParts, loading,
      getPart, getPartsByMold, getMoldsByPart,
      addTransaction, linkPartToMold,
    }}>
      {children}
    </SparePartsContext.Provider>
  );
}

export function useSpareParts() {
  const context = useContext(SparePartsContext);
  if (!context) throw new Error('useSpareParts must be used within SparePartsProvider');
  return context;
}
